'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function YuzDolgusununAlerjiRiski() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/yuz-dolgusunun-alerji-riski-nadir-ama-bilinmesi-gereken-gercekler';
  const pageTitle = 'Yüz Dolgusunun Alerji Riski: Nadir Ama Bilinmesi Gereken Gerçekler';
  const pageDescription = 'Yüz dolgu işlemleri alerji yapar mı? Belirtiler, risk grupları ve doğru ürün seçimi üzerine bilimsel ve klinik açıklamalar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Yüz Dolgusunun Alerji Riski', href: '/tr/blog/yuz-dolgusunun-alerji-riski-nadir-ama-bilinmesi-gereken-gercekler' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Yüz dolgusu alerji riski ve güvenlik değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Yüz Dolgusunun Alerji Riski: Nadir Ama Bilinmesi Gereken Gerçekler
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz dolgusu, yaşla birlikte azalan hacmi geri kazandıran ve yüze genç bir form veren en yaygın medikal estetik uygulamalardan biridir.
                Ancak zaman zaman bazı hastalar "Dolguya alerjim olur mu?" sorusunu gündeme getirir.
                Bu yazı, gerçek alerji riskini bilimsel verilerle açıklıyor.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Dolgu Maddesinin Yapısı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Modern yüz dolgularının çoğu hyaluronik asit (HA) bazlıdır.
                Bu molekül vücudumuzda zaten bulunur ve su tutma kapasitesiyle deriye hacim kazandırır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Yani vücut için yabancı değil, tanıdık bir maddedir.
                Bu nedenle gerçek alerjik reaksiyon olasılığı son derece düşüktür.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Gerçek Alerji ve Normal Tepkiler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-emerald-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Gerçek Alerji mi?</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon travmasıdır, 24 saat içinde geçer</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem ve su tutulumudur</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">🔸 Belki</td>
                      <td className="border border-gray-300 px-4 py-3">Lidokain duyarlılığı olabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nodül veya sertlik</td>
                      <td className="border border-gray-300 px-4 py-3">🔸 Belki</td>
                      <td className="border border-gray-300 px-4 py-3">Geçici lokal reaksiyon</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Döküntü, solunum zorluğu</td>
                      <td className="border border-gray-300 px-4 py-3">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Gerçek alerji – çok nadir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 İstatistiksel olarak her 10 000 vakanın yalnızca 1–2'sinde gerçek alerji görülür.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Alerjiye Sebep Olabilecek Etkenler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Lidokain içeren dolgu</td>
                      <td className="border border-gray-300 px-4 py-3">Bazı hastalarda lokal anestezik alerjisi olabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hayvansal HA kaynaklı eski ürünler</td>
                      <td className="border border-gray-300 px-4 py-3">Artık kullanılmıyor, ama geçmişte alerjiye neden olabiliyordu</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Otoimmün hastalıklar</td>
                      <td className="border border-gray-300 px-4 py-3">Bağışıklık sistemi reaktif çalışır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Enfekte bölgede uygulama</td>
                      <td className="border border-gray-300 px-4 py-3">Bağışıklık lokal tepki verir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Bilimsel Veriler
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                ISAPS (2024) verilerine göre:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Hafif alerji oranı % 0,02</li>
                  <li>Ciddi reaksiyon oranı % 0,003</li>
                  <li>En sık neden: Lidokain duyarlılığı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Belirtiler ve Zaman Çizelgesi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Zaman</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Olası Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">0–6 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">Geçici reaksiyon</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">6–24 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Kızarıklık, şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">Normal inflamasyon</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">24–48 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Sertlik veya nodül</td>
                      <td className="border border-gray-300 px-4 py-3">Aşırı doku reaksiyonu</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dakikalar</td>
                      <td className="border border-gray-300 px-4 py-3">Solunum güçlüğü</td>
                      <td className="border border-gray-300 px-4 py-3">Acil durum (çok nadir)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Vaka
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastamda dolgu sonrası hafif kaşıntı ve ısı artışı görüldü.
                  Muayenede alerji değil, lidokain duyarlılığı çıktı.
                  Antihistaminik ile tamamen düzeldi."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Alerji Testi Gerekir mi?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Evet. Geçmişinde alerjik reaksiyon yaşayan kişilerde ön test önerilir.
                Kol içine çok küçük miktarda dolgu enjekte edilip 30 dakika izlenir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Tedavi Seçenekleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-yellow-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Uygulama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif reaksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Antihistaminik, soğuk kompres</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Bölgesel ödem</td>
                      <td className="border border-gray-300 px-4 py-3">Kortizon krem veya oral steroid</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nodül oluşumu</td>
                      <td className="border border-gray-300 px-4 py-3">Hyaluronidaz enzimi ile dolgu eritilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sistemik reaksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Acil adrenalin müdahalesi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Hyaluronidaz Enzimi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu alerjisinde veya istenmeyen sonuçlarda en etkili çözümdür.
                Hyaluronik asidi parçalayarak vücuttan temizler, dokuya zarar vermez.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Uzman Görüşü
              </h2>
              
              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <p className="text-green-800 italic">
                  "Yüz dolgusu alerjisi son derece nadir.
                  En önemli nokta, steril çalışmak ve FDA onaylı ürün kullanmaktır."
                </p>
                <p className="text-green-800 font-medium mt-4">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Sık Sorulan Sorular
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Yüz dolgu alerjisi kalıcı mı?</h3>
                  <p className="text-gray-600">Hayır, tedavi ile tamamen düzelir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Alerji testi her hastaya gerekli mi?</h3>
                  <p className="text-gray-600">Hayır, sadece duyarlılığı olanlara.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Dolgu markası fark eder mi?</h3>
                  <p className="text-gray-600">Evet, saflık oranı yüksek markalar daha güvenlidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Alerji çıkarsa ne yapılır?</h3>
                  <p className="text-gray-600">Dolgu eritilir ve anti-alerjik tedavi uygulanır.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hyaluronidaz nedir?</h3>
                  <p className="text-gray-600">Hyaluronik asidi parçalayan enzimdir, alerji durumunda dolguyu güvenle eritir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Alerji riski ne kadar?</h3>
                  <p className="text-gray-600">Her 10.000 vakada sadece 1-2 kişide görülür, çok nadirdir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusu son derece güvenli bir uygulamadır; alerji riski yok denecek kadar azdır.
                Ancak hastanın öyküsünü dikkatle dinleyen ve doğru ürün seçen hekim fark yaratır.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Güzellik tesadüf değil, bilinçtir.
                  Ne uygulandığını ve vücuduna ne girdiğini bil."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Dermal Filler Allergy Incidence Report"</li>
                  <li>FDA (2023) – "Hypersensitivity Reactions to Hyaluronic Acid Fillers"</li>
                  <li>Teoxane Research (2024) – "Lidocaine Sensitivity in Facial Fillers"</li>
                  <li>TETD (2024) – "Yüz Dolgu Uygulamalarında Güvenlik Kılavuzu"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#yüzdolgu</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#alerji</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#estetikgüvenlik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Yüz Dolgusu Uygulaması ve Güvenlik Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan yüz dolgusu uygulaması ve alerji riski hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Yüz dolgusu uygulaması ve alerji riski hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-emerald-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusunun-alerji-riski-gercek-olgular-yanlis-algilar-ve-guvenli-uygulama"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusunun Alerji Riski</h4>
              <p className="text-gray-600 text-sm">Dudak dolgu alerji yapar mı? Hyaluronik asit reaksiyonları ve güvenli uygulama kriterleri.</p>
            </a>
            <a
              href="/tr/blog/botoksun-alerji-riski-gercek-tehlike-mi-yanlis-algi-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoksun Alerji Riski</h4>
              <p className="text-gray-600 text-sm">Botoks alerjisi gerçek tehlike mi, yanlış algı mı? Bilimsel veriler ve klinik gözlemler.</p>
            </a>
            <a
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe nedir, hangi içerikli ürünler kullanılır? Hacim artırıcı dolgular ve uygulama teknikleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
